import { useState, useEffect } from 'react';
import {
  UserPlus, Shield, ShieldCheck, Code2, Copy, Trash2, Loader2,
  Mail, RotateCw,
} from 'lucide-react';
import { api, AdminUser } from '../api';
import { useAuth } from '../context/AuthContext';
import { PageHeader, StatusBadge, useToast, ToastContainer } from '../components/ui';

const LEVEL_LABELS: Record<string, { label: string; color: string; icon: React.ReactNode }> = {
  super_admin: { label: 'Super Admin', color: '#FFD400', icon: <ShieldCheck className="w-3.5 h-3.5" /> },
  admin:       { label: 'Admin',       color: '#a78bfa', icon: <Shield className="w-3.5 h-3.5" /> },
  developer:   { label: 'Developer',   color: '#38bdf8', icon: <Code2 className="w-3.5 h-3.5" /> },
};

function formatDate(d: string) {
  return new Date(d).toLocaleDateString('en-US', { month: 'short', day: 'numeric', year: 'numeric' });
}

function Modal({ isOpen, onClose, title, children }: {
  isOpen: boolean; onClose: () => void; title: string; children: React.ReactNode;
}) {
  if (!isOpen) return null;
  return (
    <div className="fixed inset-0 z-50 flex items-center justify-center p-4">
      <div className="absolute inset-0 bg-black/70 backdrop-blur-sm" onClick={onClose} />
      <div className="w-full max-w-md relative z-10 max-h-[90vh] overflow-y-auto p-6"
        style={{ backgroundColor: '#111', border: '1px solid rgba(255,255,255,0.08)', borderRadius: 4 }}>
        <div className="flex items-center justify-between mb-5">
          <h2 className="text-lg font-display font-bold text-white">{title}</h2>
          <button onClick={onClose} className="text-white/30 hover:text-white/60 transition-colors">
            <svg className="w-5 h-5" fill="none" viewBox="0 0 24 24" stroke="currentColor">
              <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M6 18L18 6M6 6l12 12" />
            </svg>
          </button>
        </div>
        {children}
      </div>
    </div>
  );
}

export default function AdminManageUsers() {
  const { user: currentUser } = useAuth();
  const { addToast, toasts, removeToast } = useToast();

  const [users, setUsers] = useState<AdminUser[]>([]);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState('');

  // Invite modal
  const [showInvite, setShowInvite] = useState(false);
  const [inviteEmail, setInviteEmail] = useState('');
  const [inviteName, setInviteName] = useState('');
  const [inviteLevel, setInviteLevel] = useState<string>('admin');
  const [inviteLoading, setInviteLoading] = useState(false);
  const [inviteError, setInviteError] = useState('');
  const [inviteResult, setInviteResult] = useState<{ url: string; emailSent: boolean; emailError?: string } | null>(null);

  // Delete modal
  const [showDelete, setShowDelete] = useState(false);
  const [userToDelete, setUserToDelete] = useState<AdminUser | null>(null);
  const [deleteLoading, setDeleteLoading] = useState(false);

  // Resend loading state
  const [resendingId, setResendingId] = useState<number | null>(null);

  useEffect(() => { loadUsers(); }, []);

  const loadUsers = async () => {
    try {
      const { users: data } = await api.getAdminUsers();
      setUsers(data);
    } catch (err) {
      setError(err instanceof Error ? err.message : 'Failed to load users');
    } finally {
      setLoading(false);
    }
  };

  const handleInvite = async (e: React.FormEvent) => {
    e.preventDefault();
    if (!inviteEmail) return;
    setInviteLoading(true);
    setInviteError('');
    setInviteResult(null);
    try {
      const result = await api.inviteAdminUser({
        email: inviteEmail,
        name: inviteName || undefined,
        admin_level: inviteLevel,
      });
      setInviteResult({
        url: result.invite_url,
        emailSent: result.email_sent,
        emailError: result.email_error,
      });
      addToast(result.email_sent ? 'Invitation sent' : 'User created — share the invite link manually', result.email_sent ? 'success' : 'warning');
      loadUsers();
    } catch (err) {
      setInviteError(err instanceof Error ? err.message : 'Failed to invite user');
    } finally {
      setInviteLoading(false);
    }
  };

  const handleResend = async (userId: number) => {
    setResendingId(userId);
    try {
      const result = await api.resendAdminInvite(userId);
      if (result.email_sent) {
        addToast('Invite re-sent', 'success');
      } else {
        addToast(`Email failed — invite link: ${result.invite_url}`, 'warning');
      }
    } catch (err) {
      addToast(err instanceof Error ? err.message : 'Failed to resend invite', 'error');
    } finally {
      setResendingId(null);
    }
  };

  const handleDelete = async () => {
    if (!userToDelete) return;
    setDeleteLoading(true);
    try {
      await api.deleteAdminUser(userToDelete.id);
      addToast(`${userToDelete.email} removed`, 'success');
      setShowDelete(false);
      setUserToDelete(null);
      loadUsers();
    } catch (err) {
      addToast(err instanceof Error ? err.message : 'Failed to delete user', 'error');
    } finally {
      setDeleteLoading(false);
    }
  };

  const copyToClipboard = (text: string) => {
    navigator.clipboard.writeText(text);
    addToast('Copied to clipboard', 'success');
  };

  const isSuperAdmin = currentUser?.admin_level === 'super_admin';

  return (
    <>
      <div className="p-6 lg:p-8 max-w-5xl mx-auto space-y-6">
        <PageHeader
          title="Manage Users"
          subtitle={isSuperAdmin ? 'Invite and manage admin team members' : 'View admin team members'}
          actions={
            isSuperAdmin ? (
              <button onClick={() => { setShowInvite(true); setInviteEmail(''); setInviteName(''); setInviteLevel('admin'); setInviteError(''); setInviteResult(null); }}
                className="btn-primary flex items-center gap-2 text-sm">
                <UserPlus className="w-4 h-4" /> Invite Admin
              </button>
            ) : null
          }
        />

        {/* Current user info */}
        <div className="p-4 flex items-center gap-3"
          style={{ backgroundColor: 'rgba(255,212,0,0.04)', border: '1px solid rgba(255,212,0,0.1)', borderRadius: 4 }}>
          <ShieldCheck className="w-5 h-5 shrink-0" style={{ color: '#FFD400' }} />
          <div>
            <p className="text-sm text-white">
              Logged in as <strong>{currentUser?.email}</strong>
            </p>
            <p className="text-xs mt-0.5" style={{ color: 'rgba(255,255,255,0.35)' }}>
              {isSuperAdmin
                ? 'Super Admin — full management access'
                : `${currentUser?.admin_level === 'admin' ? 'Admin' : currentUser?.admin_level || 'Admin'} — read-only view`}
            </p>
          </div>
        </div>

        {error && (
          <div className="p-3 text-sm rounded" style={{ backgroundColor: 'rgba(248,113,113,0.08)', border: '1px solid rgba(248,113,113,0.18)', color: '#f87171' }}>
            {error}
          </div>
        )}

        {/* Users list */}
        {loading ? (
          <div className="flex items-center justify-center py-16">
            <Loader2 className="w-6 h-6 animate-spin" style={{ color: '#FFD400' }} />
          </div>
        ) : users.length === 0 ? (
          <div className="text-center py-16" style={{ color: 'rgba(255,255,255,0.25)' }}>
            <p className="text-sm">No admin users found.</p>
          </div>
        ) : (
          <div className="space-y-2">
            {users.map(u => {
              const level = LEVEL_LABELS[u.admin_level || 'admin'] || LEVEL_LABELS.admin;
              const isCurrentUser = u.id === currentUser?.id;

              return (
                <div key={u.id} className="flex items-center gap-4 px-5 py-4"
                  style={{ backgroundColor: 'rgba(255,255,255,0.015)', border: '1px solid rgba(255,255,255,0.06)', borderRadius: 4 }}>
                  {/* Avatar */}
                  <div className="w-10 h-10 rounded-full shrink-0 flex items-center justify-center"
                    style={{ backgroundColor: `${level.color}10`, border: `2px solid ${level.color}25` }}>
                    <span className="text-sm font-bold" style={{ color: level.color }}>
                      {(u.name || u.email).charAt(0).toUpperCase()}
                    </span>
                  </div>

                  {/* Info */}
                  <div className="flex-1 min-w-0">
                    <div className="flex items-center gap-2">
                      <span className="text-sm font-semibold text-white truncate">
                        {u.name || u.email.split('@')[0]}
                      </span>
                      {isCurrentUser && (
                        <span className="text-[10px] px-1.5 py-0.5 font-semibold"
                          style={{ backgroundColor: 'rgba(255,212,0,0.1)', color: '#FFD400', borderRadius: 2 }}>
                          You
                        </span>
                      )}
                    </div>
                    <p className="text-xs mt-0.5" style={{ color: 'rgba(255,255,255,0.3)' }}>{u.email}</p>
                  </div>

                  {/* Role badge */}
                  <div className="flex items-center gap-1.5 px-2.5 py-1 shrink-0"
                    style={{ backgroundColor: `${level.color}10`, borderRadius: 3, color: level.color }}>
                    {level.icon}
                    <span className="text-xs font-semibold">{level.label}</span>
                  </div>

                  {/* Account status */}
                  <div className="w-20 shrink-0">
                    <StatusBadge variant={u.has_password ? 'success' : 'warning'}>
                      {u.has_password ? 'Active' : 'Invited'}
                    </StatusBadge>
                  </div>

                  {/* Created date */}
                  <span className="text-xs shrink-0 hidden lg:block w-24 text-right" style={{ color: 'rgba(255,255,255,0.2)' }}>
                    {formatDate(u.created_at)}
                  </span>

                  {/* Actions */}
                  <div className="flex gap-1.5 shrink-0">
                    {isSuperAdmin && !u.has_password && (
                      <button onClick={() => handleResend(u.id)} disabled={resendingId === u.id}
                        title="Resend invite"
                        className="p-1.5 rounded transition-colors hover:bg-white/5"
                        style={{ color: 'rgba(255,255,255,0.3)' }}>
                        {resendingId === u.id ? <Loader2 className="w-3.5 h-3.5 animate-spin" /> : <RotateCw className="w-3.5 h-3.5" />}
                      </button>
                    )}
                    {isSuperAdmin && !isCurrentUser && (
                      <button onClick={() => { setUserToDelete(u); setShowDelete(true); }}
                        title="Delete user"
                        className="p-1.5 rounded transition-colors hover:bg-white/5"
                        style={{ color: 'rgba(248,113,113,0.5)' }}>
                        <Trash2 className="w-3.5 h-3.5" />
                      </button>
                    )}
                  </div>
                </div>
              );
            })}
          </div>
        )}
      </div>

      {/* ── Invite Modal ────────────────────────────────────────────────────── */}
      <Modal isOpen={showInvite} onClose={() => setShowInvite(false)} title="Invite Admin User">
        <form onSubmit={handleInvite} className="space-y-4">
          {inviteError && (
            <div className="p-3 rounded text-sm" style={{ backgroundColor: 'rgba(248,113,113,0.08)', border: '1px solid rgba(248,113,113,0.18)', color: '#f87171' }}>
              {inviteError}
            </div>
          )}

          {inviteResult && (
            <div className="p-4 rounded space-y-2" style={{
              backgroundColor: inviteResult.emailSent ? 'rgba(74,222,128,0.08)' : 'rgba(251,146,60,0.08)',
              border: `1px solid ${inviteResult.emailSent ? 'rgba(74,222,128,0.18)' : 'rgba(251,146,60,0.18)'}`,
            }}>
              <p className="text-sm font-semibold" style={{ color: inviteResult.emailSent ? '#4ade80' : '#fb923c' }}>
                {inviteResult.emailSent ? 'Invitation email sent!' : 'User created — email could not be sent'}
              </p>
              {inviteResult.emailError && (
                <p className="text-xs" style={{ color: 'rgba(255,255,255,0.3)' }}>{inviteResult.emailError}</p>
              )}
              <div className="flex gap-2">
                <input type="text" value={inviteResult.url} readOnly className="input flex-1 font-mono text-xs" />
                <button type="button" onClick={() => copyToClipboard(inviteResult.url)} className="btn-secondary p-2">
                  <Copy className="w-4 h-4" />
                </button>
              </div>
            </div>
          )}

          {!inviteResult && (
            <>
              <div>
                <label className="block text-xs font-medium mb-1" style={{ color: 'rgba(255,255,255,0.4)' }}>
                  Email *
                </label>
                <input type="email" value={inviteEmail} onChange={e => setInviteEmail(e.target.value)}
                  className="input w-full" placeholder="admin@example.com" required />
              </div>
              <div>
                <label className="block text-xs font-medium mb-1" style={{ color: 'rgba(255,255,255,0.4)' }}>
                  Name (optional)
                </label>
                <input type="text" value={inviteName} onChange={e => setInviteName(e.target.value)}
                  className="input w-full" placeholder="Jane Smith" />
              </div>
              <div>
                <label className="block text-xs font-medium mb-1" style={{ color: 'rgba(255,255,255,0.4)' }}>
                  Role
                </label>
                <div className="flex gap-2">
                  {(['super_admin', 'admin', 'developer'] as const).map(level => {
                    const meta = LEVEL_LABELS[level];
                    const active = inviteLevel === level;
                    return (
                      <button key={level} type="button" onClick={() => setInviteLevel(level)}
                        className="flex-1 flex items-center justify-center gap-1.5 py-2.5 text-xs font-semibold rounded transition-colors"
                        style={{
                          backgroundColor: active ? `${meta.color}15` : 'rgba(255,255,255,0.03)',
                          border: `1px solid ${active ? `${meta.color}30` : 'rgba(255,255,255,0.06)'}`,
                          color: active ? meta.color : 'rgba(255,255,255,0.3)',
                        }}>
                        {meta.icon} {meta.label}
                      </button>
                    );
                  })}
                </div>
              </div>
              <div className="flex justify-end gap-3 pt-2">
                <button type="button" onClick={() => setShowInvite(false)} className="btn-secondary text-sm">Cancel</button>
                <button type="submit" disabled={inviteLoading} className="btn-primary text-sm flex items-center gap-2">
                  {inviteLoading ? <Loader2 className="w-3.5 h-3.5 animate-spin" /> : <Mail className="w-3.5 h-3.5" />}
                  {inviteLoading ? 'Sending...' : 'Send Invitation'}
                </button>
              </div>
            </>
          )}

          {inviteResult && (
            <div className="flex justify-end pt-2">
              <button type="button" onClick={() => { setShowInvite(false); setInviteResult(null); }} className="btn-secondary text-sm">
                Done
              </button>
            </div>
          )}
        </form>
      </Modal>

      {/* ── Delete Modal ────────────────────────────────────────────────────── */}
      <Modal isOpen={showDelete} onClose={() => { setShowDelete(false); setUserToDelete(null); }} title="Remove Admin User">
        {userToDelete && (
          <div className="space-y-5">
            <div className="p-4 rounded" style={{ backgroundColor: 'rgba(248,113,113,0.08)', border: '1px solid rgba(248,113,113,0.18)' }}>
              <p className="text-sm font-semibold mb-1" style={{ color: '#f87171' }}>This action cannot be undone</p>
              <p className="text-sm" style={{ color: 'rgba(255,255,255,0.5)' }}>
                Remove <strong className="text-white">{userToDelete.name || userToDelete.email}</strong> from the admin team?
              </p>
            </div>
            <ul className="text-xs space-y-1" style={{ color: 'rgba(255,255,255,0.3)' }}>
              <li>• Deletes their account and all credentials</li>
              <li>• Revokes all active sessions</li>
            </ul>
            <div className="flex gap-3">
              <button onClick={() => { setShowDelete(false); setUserToDelete(null); }} className="btn-secondary flex-1 text-sm" disabled={deleteLoading}>
                Cancel
              </button>
              <button onClick={handleDelete} disabled={deleteLoading}
                className="flex-1 px-4 py-2.5 text-sm font-semibold rounded transition-colors"
                style={{ backgroundColor: '#f87171', color: '#fff' }}>
                {deleteLoading ? 'Removing...' : 'Remove User'}
              </button>
            </div>
          </div>
        )}
      </Modal>

      <ToastContainer toasts={toasts} removeToast={removeToast} />
    </>
  );
}
